import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import { useEffect, useState } from 'react'
import { FeatureFlag, FeatureFlagConfig } from '~/types/experiments'

export let experiment: ExperimentClient | null = null

// Needed to trigger update feature flag value after full initialization
let markAsInitialized: () => void
let markAsFailed: () => void
let amplitudeState: 'initialized' | 'failed' | 'pending' = 'pending'

const initializedPromise = new Promise<void>((resolve, reject) => {
  markAsInitialized = () => {
    amplitudeState = 'initialized'
    resolve()
  }
  markAsFailed = () => {
    amplitudeState = 'failed'
    reject()
  }
})

export const initializeExperimentClient = () => {
  const amplitudeApiKey = (window as any).ENV?.AMPLITUDE_API_KEY

  experiment = Experiment.initializeWithAmplitudeAnalytics(amplitudeApiKey, { serverZone: 'EU' })

  experiment
    .fetch()
    .catch(() => markAsFailed())
    .then(() => {
      markAsInitialized()
      debugLogFeatureFlags()
    })
}

export const getFeatureFlag = <T extends FeatureFlag>(flag: T): FeatureFlagConfig[T] | null => {
  if (!experiment) {
    return null
  }

  const variant = experiment.variant(flag)

  if (isObjectEmpty(variant)) {
    return null
  }

  return variant as FeatureFlagConfig[T]
}

export const useFeatureFlag = <T extends FeatureFlag>(flag: T) => {
  const [flagVariant, setFlagVariant] = useState<FeatureFlagConfig[T] | null>(null)

  const updateFlagVariant = () => {
    const variant = getFeatureFlag<T>(flag)
    setFlagVariant(variant)
  }

  useEffect(() => {
    if (amplitudeState === 'pending') {
      initializedPromise.then(() => updateFlagVariant()).catch(() => {})
    }
  }, [])

  useEffect(() => {
    updateFlagVariant()
  }, [flag])

  return flagVariant
}

export const useFeatureFlagActive = <T extends FeatureFlag>(flag: T): boolean => {
  const flagVariant = useFeatureFlag(flag)

  return flagVariant !== null && !!flagVariant?.value
}

export const useFeatureFlagVariantActive = <T extends FeatureFlag>(
  flag: T,
  valueSelector: FeatureFlagConfig[T]['value'],
): boolean => {
  const flagVariant = useFeatureFlag(flag)

  if (flagVariant === null) {
    return false
  }

  const variant = flagVariant.value

  return variant === valueSelector
}

function isObjectEmpty(obj: object): boolean {
  return Object.keys(obj).length === 0
}

// Debug log feature flags
function debugLogFeatureFlags() {
  const text = [
    'Feature Flags:',
    ...Object.values(FeatureFlag).map((flag) => `${flag}: ${experiment?.variant(flag)?.value}`),
  ].join('\n')

  // console.log(text)
}
